import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '../../../../util/reactIntl';
import RatingSummary from '../RatingSummary/RatingSummary';
import ReviewCard from '../../../../components/ReviewCard/ReviewCard';
import ReviewsSubTabNavigation from '../ReviewsSubTabNavigation/ReviewsSubTabNavigation';
import css from './ReviewsReceivedView.module.css';

/**
 * ReviewsReceivedView component displays reviews where store is provider
 */
const ReviewsReceivedView = ({
    reviews,
    achievementReviews,
    totalReviews,
    reviewSummary,
    inProgress,
    error,
    intl,
    onLoadMore,
    pagination,
    // Navigation props
    activeSubTab,
    totalReviewsReceived,
    totalReviewsGiven,
    onSubTabChange,
}) => {
    // ── Hooks must be called unconditionally (Rules of Hooks) ────────────────
    // observerTarget and the IntersectionObserver effect are declared here,
    // before any early returns, so the hooks call count never changes between
    // renders regardless of loading/empty/populated state.
    const observerTarget = useRef(null);
    const hasMore = pagination && pagination.page < pagination.totalPages;

    useEffect(() => {
        if (!hasMore || inProgress) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !inProgress) {
                    onLoadMore();
                }
            },
            { threshold: 0.1 }
        );

        const currentTarget = observerTarget.current;
        if (currentTarget) {
            observer.observe(currentTarget);
        }

        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget);
            }
        };
    }, [hasMore, inProgress, onLoadMore]);
    // ─────────────────────────────────────────────────────────────────────────

    if (inProgress && reviews.length === 0) {
        return (
            <div className={css.container}>
                <div className={css.loadingState}>
                    <FormattedMessage
                        id="StoreProfilePage.reviews.loading"
                        defaultMessage="Loading reviews..."
                    />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={css.container}>
                <div className={css.errorState}>
                    <FormattedMessage
                        id="StoreProfilePage.reviews.error"
                        defaultMessage="Error loading reviews. Please try again."
                    />
                </div>
            </div>
        );
    }

    if (reviews.length === 0 && achievementReviews.length === 0) {
        return (
            <div className={css.container}>
                <div className={css.reviewsLayout}>
                    <div className={css.reviewsMain}>
                        {/* Sub-tab Navigation */}
                        <ReviewsSubTabNavigation
                            activeSubTab={activeSubTab}
                            totalReviewsReceived={totalReviewsReceived}
                            totalReviewsGiven={totalReviewsGiven}
                            onSubTabChange={onSubTabChange}
                        />
                        <div className={css.emptyState}>
                            <FormattedMessage
                                id="StoreProfilePage.reviews.empty.received"
                                defaultMessage="No reviews received yet."
                            />
                        </div>
                    </div>

                </div>
            </div>
        );
    }

    const hasSummaryData =
        Number.isFinite(Number(reviewSummary?.reviewCount)) &&
        Number(reviewSummary?.reviewCount) > 0 &&
        Number.isFinite(Number(reviewSummary?.averageRating)) &&
        reviewSummary?.starCounts &&
        typeof reviewSummary.starCounts === 'object';
    const achievementRatings = (achievementReviews || [])
        .map(r => Number(r?.attributes?.rating))
        .filter(r => Number.isFinite(r) && r >= 1 && r <= 5);
    const achievementReviewCount = achievementRatings.length;
    const achievementRatingSum = achievementRatings.reduce((sum, rating) => sum + rating, 0);
    const achievementDistribution = achievementRatings.reduce(
        (acc, rating) => {
            const rounded = Math.round(rating);
            if (rounded >= 1 && rounded <= 5) {
                acc[rounded] = (acc[rounded] || 0) + 1;
            }
            return acc;
        },
        { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
    );
    const mergedSummaryData = hasSummaryData
        ? (() => {
            const baseReviewCount = Number(reviewSummary?.reviewCount) || 0;
            const mergedReviewCount = baseReviewCount + achievementReviewCount;
            const baseAverage = Number(reviewSummary?.averageRating) || 0;
            const baseRatingSum = baseAverage * baseReviewCount;
            const mergedRatingSum = baseRatingSum + achievementRatingSum;
            const mergedAverage =
                mergedReviewCount > 0
                    ? Number((mergedRatingSum / mergedReviewCount).toFixed(1))
                    : 0;
            const starCounts = reviewSummary?.starCounts || {};
            const mergedStarCounts = {
                1: Number(starCounts[1] ?? starCounts['1'] ?? 0) + achievementDistribution[1],
                2: Number(starCounts[2] ?? starCounts['2'] ?? 0) + achievementDistribution[2],
                3: Number(starCounts[3] ?? starCounts['3'] ?? 0) + achievementDistribution[3],
                4: Number(starCounts[4] ?? starCounts['4'] ?? 0) + achievementDistribution[4],
                5: Number(starCounts[5] ?? starCounts['5'] ?? 0) + achievementDistribution[5],
            };
            return {
                ...reviewSummary,
                reviewCount: mergedReviewCount,
                averageRating: mergedAverage,
                starCounts: mergedStarCounts,
            };
        })()
        : null;

    const distributionIncomplete =
        !hasSummaryData &&
        (inProgress ||
            (pagination &&
                pagination.totalPages > 0 &&
                pagination.page < pagination.totalPages));

    // Sort reviews by newest first
    const sortedReviews = [...reviews].sort((a, b) => {
        const dateA = new Date(a.attributes?.createdAt || 0);
        const dateB = new Date(b.attributes?.createdAt || 0);
        return dateB - dateA; // Newest first
    });

    const mergedReviews = [...sortedReviews, ...(achievementReviews || [])];


    const allReviews = [...mergedReviews];
    const totalReceivedWithAchievements =
        Number(totalReviewsReceived || 0);

    return (
        <div className={css.container}>
            <div className={css.reviewsLayout}>
                {/* Rating Summary Sidebar */}
                {allReviews.length > 0 && <div className={css.reviewsSidebar}>
                    <RatingSummary
                        reviews={allReviews}
                        totalReviewsCount={totalReceivedWithAchievements}
                        summaryData={mergedSummaryData}
                        distributionIncomplete={distributionIncomplete}
                    />
                </div>}

                {/* Reviews Main */}
                <div className={css.reviewsMain}>


                    <div className={css.reviewsHeader}>
                        <h3 className={css.reviewsTitle}>
                            <FormattedMessage
                                id="StoreProfilePage.tabs.reviews.title"
                                defaultMessage="All Reviews"
                            />
                        </h3>
                        <p className={css.reviewsSubtitle}>
                            <FormattedMessage
                                id="StoreProfilePage.reviews.subtitle"
                                defaultMessage="Reviews and ratings shared by customers after renting from this store."
                            />
                        </p>
                    </div>
                    {/* Sub-tab Navigation */}
                    <ReviewsSubTabNavigation
                        activeSubTab={activeSubTab}
                        totalReviewsReceived={totalReceivedWithAchievements}
                        totalReviewsGiven={totalReviewsGiven}
                        onSubTabChange={onSubTabChange}
                    />
                    <div className={css.reviewsList}>
                        {allReviews.map((review, index) => (
                            <ReviewCard
                                key={review.id?.uuid || review.id || index}
                                review={review}
                                intl={intl}
                                showRentalContext={true}
                            />
                        ))}
                    </div>

                    {/* Infinite scroll trigger */}
                    {hasMore && (
                        <div ref={observerTarget} className={css.infiniteScrollTrigger}>
                            {inProgress && (
                                <div className={css.loadingIndicator}>
                                    <FormattedMessage
                                        id="StoreProfilePage.reviews.loading"
                                        defaultMessage="Loading..."
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

ReviewsReceivedView.propTypes = {
    reviews: PropTypes.arrayOf(PropTypes.object).isRequired,
    totalReviews: PropTypes.number.isRequired,
    reviewSummary: PropTypes.shape({
        averageRating: PropTypes.number,
        reviewCount: PropTypes.number,
        starCounts: PropTypes.shape({
            1: PropTypes.number,
            2: PropTypes.number,
            3: PropTypes.number,
            4: PropTypes.number,
            5: PropTypes.number,
        }),
    }),
    inProgress: PropTypes.bool,
    error: PropTypes.object,
    intl: intlShape.isRequired,
    onLoadMore: PropTypes.func,
    pagination: PropTypes.shape({
        page: PropTypes.number,
        perPage: PropTypes.number,
        totalPages: PropTypes.number,
    }),
    // Navigation props
    activeSubTab: PropTypes.string.isRequired,
    totalReviewsReceived: PropTypes.number,
    totalReviewsGiven: PropTypes.number,
    onSubTabChange: PropTypes.func.isRequired,
};

export default injectIntl(ReviewsReceivedView);

